import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { LandingNav } from '@/components/landing/LandingNav';
import { LandingHero } from '@/components/landing/LandingHero';
import { LandingPrograms } from '@/components/landing/LandingPrograms';
import { LandingStatistics } from '@/components/landing/LandingStatistics';
import { LandingProfile } from '@/components/landing/LandingProfile';
import { LandingNews } from '@/components/landing/LandingNews';
import { LandingArticles } from '@/components/landing/LandingArticles';
import { LandingPartners } from '@/components/landing/LandingPartners';
import { LandingContact } from '@/components/landing/LandingContact';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { useLanguage } from '@/contexts/LanguageContext';

interface SectionData {
  sectionId: string;
  isVisible: boolean;
  order: number;
}

// Map section IDs to their components
const sectionComponents: Record<string, React.ComponentType> = {
  hero: LandingHero,
  programs: LandingPrograms,
  statistics: LandingStatistics,
  profile: LandingProfile,
  news: LandingNews,
  articles: LandingArticles,
  partners: LandingPartners,
  contact: LandingContact,
};

export function LandingPage() {
  const [sections, setSections] = useState<SectionData[]>([]);
  const [loading, setLoading] = useState(true);
  const { dir, t } = useLanguage();

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'landing_section_visibility'));
      
      const sectionsData: SectionData[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sectionsData.push({
          sectionId: data.sectionId,
          isVisible: data.isVisible,
          order: data.order !== undefined ? data.order : 999
        });
      });

      // Sort by order
      sectionsData.sort((a, b) => a.order - b.order);
      
      setSections(sectionsData);
    } catch (error) {
      console.error('Error loading sections:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white" dir={dir}>
        <LandingNav />
        <main className="flex items-center justify-center h-screen">
          <div className="text-center">
            <p className="text-gray-600">{t('landing.common.loading')}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir={dir}>
      <LandingNav />
      <main>
        {sections
          .filter(section => section.isVisible)
          .map(section => {
            const Component = sectionComponents[section.sectionId];
            return Component ? <Component key={section.sectionId} /> : null;
          })}
      </main>
      <LandingFooter />
    </div>
  );
}

